import React, { useContext, useState } from 'react'
import { AppContext } from 'contexts/AppContext';
import { Accordion, AccordionSummary, AccordionDetails, Box, useMediaQuery } from '@mui/material';
import CaretDown from 'components/icons/CaretDown';
import RoundIconButton1 from 'components/elements/RoundIconButton1';
import "./Order.css"
import { translate } from 'utils/Translate';
import ExplainerText from 'components/elements/ExplainerText';
import Headline6 from 'components/elements/Headline6';
import { pointsConvertor } from 'utils/general';
import ContentNormal from 'components/elements/ContentNormal';
import Order from './Order';
import { callAPI } from 'utils/API';
import Image from 'components/elements/Image';

const Confirmation = (props) => {
    const { styleGuide, userData, brandData } = useContext(AppContext)
    const { confirmation } = props
    const [timelineEvents, setTimelineEvents] = useState([])
    const [alert, setAlert] = useState(confirmation.orders.some(o => o.alertCount)) // Order user remark has been updated
    const pointSuffix = translate("_POINTS_SHORT_", "pt")
    const isMobile = useMediaQuery('(max-width:900px)')

    const fetchOrderTimelineEvents = async () => {
        if(!timelineEvents.length){
            const orderIds = props.confirmation.orders.map(o => o.OrderID)
            const timelineEvents = await callAPI('/user/orders/timeline', 'GET', { query: { orderIds: orderIds } })
            if(timelineEvents && timelineEvents.length){
                setTimelineEvents(timelineEvents)
            }
        }
    }

    const removeAlerts = async () => {
        for (const o of confirmation.orders) {
            if (o.alertCount) {
                const updateAlertCount = await callAPI("order/updateOrderAlert", "POST", { body: { OrderID: o.OrderID } });
                if (updateAlertCount) setAlert(null)
            }
        }
    }

    const contactEmail = brandData?.contactEmail || 'support@upgrader.dk'

    return (
        <Accordion id='orders-block' onClick={removeAlerts}>
            <AccordionSummary style={{padding: isMobile ? '4px 16px' : '20px 48px' }} expandIcon={<RoundIconButton1 icon={<CaretDown fill="white" />} />} onClick={fetchOrderTimelineEvents}>
                <Box paddingY={isMobile ? 0 : 2} paddingRight={isMobile ? 1 : 4} display={'flex'} flex={1} gap={isMobile ? 2 : 4} justifyContent={'space-between'} flexWrap={'wrap'}>
                    <Box display={'flex'} gap={4}>
                        {alert && !isMobile && (
                            <Image style={{ width: 15, height: 30, padding: 10, position: 'absolute', left: 18 }} src={`url(https://i.imgur.com/8ZvWaVU.png)`} />
                        )}
                        <Box display={'flex'} flexDirection={'column'} gap={1} width={isMobile ? 100 : 120}>
                            <ExplainerText translationKey='_PROFILE_ORDER_CONFIRMATION_' fallbackText='Ordrebekræftelse' />
                            <Headline6 fallbackText={`${confirmation.ConfirmationID || '-'}`} />
                        </Box>
                        <Box display={'flex'} flexDirection={'column'} gap={1} width={isMobile ? 100 : 120}>
                            <ExplainerText translationKey='_PROFILE_ORDER_DATE_' fallbackText={'Ordredato'} />
                            <Headline6 fallbackText={`${confirmation.date || '-'}`} />
                        </Box>
                        {!!confirmation.shipmentReference &&
                            <Box display={'flex'} flexDirection={'column'} gap={1} width={isMobile ? 100 : 120}>
                                <ExplainerText translationKey='_PROFILE_SHIPMENT_REFERENCE_' fallbackText={'Forsendelsesreference'} />
                                <Headline6 fallbackText={confirmation.shipmentReference} />
                            </Box>
                        }
                    </Box>

                    {!brandData?.features?.disablePoints &&
                        <Box display={'flex'} columnGap={4} rowGap={2} flexWrap={'wrap'}>
                            {!!props.freightInfo?.enableFreightPriceInterval &&
                                <Box display={'flex'} flexDirection={'column'} gap={1} width={isMobile ? 100 : 120}>
                                    <ExplainerText translationKey='_PROFILE_ORDER_POINTS_' fallbackText={'Ordre'} />
                                    <Headline6 fallbackText={pointsConvertor(brandData, confirmation.points, pointSuffix)} />
                                </Box>
                            }

                            {!!props.freightInfo?.enableFreightPriceInterval &&
                                <Box display={'flex'} flexDirection={'column'} gap={1} width={isMobile ? 100 : 120}>
                                    <ExplainerText translationKey='_PROFILE_ORDER_FREIGHT_' fallbackText={'Fragt'} />
                                    <Headline6 fallbackText={pointsConvertor(brandData, confirmation.totalFreightPoints, pointSuffix)} />
                                </Box>
                            }

                            <Box display={'flex'} flexDirection={'column'} gap={1} width={isMobile ? 100 : 120}>
                                <ExplainerText translationKey='_PROFILE_ORDER_TOTAL_' fallbackText={'Total'} />
                                <Headline6 fallbackText={pointsConvertor(brandData, (confirmation.points + confirmation.totalFreightPoints), pointSuffix)} />
                            </Box>
                        </Box>
                    }
                </Box>
            </AccordionSummary>
            <AccordionDetails style={{borderTop: `1px solid ${styleGuide.color4}`, padding: 0}} >
                {confirmation.orders.sort((a, b) => a.OrderID - b.OrderID).map((o, i) => (
                    <Order key={o.OrderID} order={o} timelineEvents={timelineEvents.filter(e => e.parentRelationID === o.OrderID)} refresh={props.refresh} index={i+1} />
                ))}

                {/* Confirmation info boxes */}
                <Box display={'flex'} flexWrap={'wrap'} gap={isMobile ? 2 : 6} padding={isMobile ? 2 : 6}>
                    <ConfirmationInfoBox styleGuide={styleGuide}>
                        <Headline6 style={{ marginBottom: 10 }} translationKey="_BASKET_OVERVIEW_DELIVERY_INFORMATION_CAPTION_" fallbackText="Leveringsoplysninger" />
                        <ContentNormal>
                            {confirmation.orderConsigneeName || userData.userName}
                            <br />
                            {`${confirmation.orderConsigneeAddress1}, ${confirmation.orderConsigneeZipcode} ${confirmation.orderConsigneeCity}`}
                            <br />
                            {confirmation.orderConsigneeEmail || ''}
                            <br />
                            {!confirmation.orderConsigneePhone ? translate('_USER_PHONE_NOT_AVAILABLE_', 'Telefonnummer er ikke tilgængeligt') : confirmation.orderConsigneePhone}
                        </ContentNormal>
                    </ConfirmationInfoBox>

                    <ConfirmationInfoBox styleGuide={styleGuide}>
                        <Headline6 style={{ marginBottom: 10 }} translationKey="_PROFILE_TNT_INFO_HEADLINE_" fallbackText="Track & Trace eller Dropship" />
                        <ContentNormal translationKey="_PROFILE_TNT_INFO_" fallbackText="I ordrebekræftelsen kan du tilgå track & trace på varer der afsendes fra vores lager. Ved dropship vil du i stedet modtage informationerne direkte fra vores leverandør." />
                    </ConfirmationInfoBox>

                    <ConfirmationInfoBox styleGuide={styleGuide}>
                        <Headline6 style={{ marginBottom: 10 }} translationKey="_PROFILE_ORDER_CONTACT_HEADLINE_" fallbackText="Har du spørgsmål til din ordre?" />
                        <ContentNormal translationKey="_PROFILE_ORDER_CONTACT_INFO_" fallbackText="Du er altid velkommen til at kontakte os." />
                        <Box marginTop={2} display={'flex'} flexDirection={'column'} gap={1}>
                            <a href={`mailto:${contactEmail}`} style={{textDecoration: 'none'}}>
                                <ContentNormal fallbackText={contactEmail} />
                            </a>
                            {brandData.contactPhone && <ContentNormal fallbackText={brandData.contactPhone} />}
                        </Box>
                    </ConfirmationInfoBox>
                </Box>
            </AccordionDetails>
        </Accordion>
    )
}

const ConfirmationInfoBox = (props) => {
    const isMobile = useMediaQuery('(max-width:900px)')
    return (
        <Box
            flex={1}
            display={'flex'}
            flexDirection={'column'}
            backgroundColor={props.styleGuide.color2}
            padding={isMobile ? 3 : 4}
            paddingBottom={8}
            gap={1}
            borderRadius={'14px'}
            minWidth={250}
        >
            {props.children}
        </Box>
    )
}

export default Confirmation;